import { useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import { useVirtueGuide } from "@/components/VirtueGuideContext";

/**
 * VirtueGuideHighlighter — global text-node walker.
 *
 * Wraps its children and, on mount + on every DOM mutation, replaces every
 * occurrence of the exact phrase "Virtue Guide" inside descendant TEXT NODES
 * with a styled clickable <button.vg-brand> element. Works for:
 *   - Static JSX text (Home, Footer, translations…)
 *   - Journal MDX bodies
 *   - DB-backed articles (AutoSEO imports, future SEO)
 *   - Any content added later, without per-file edits
 *
 * Safety:
 *   - Skips <a>, <button>, <code>, <pre>, <script>, <style>, <textarea>,
 *     <input>, <title>, and elements marked [data-vg-skip].
 *   - Idempotent: already-wrapped nodes are not re-wrapped.
 *   - Disabled on `/admin*` routes.
 *   - Event delegation → one click handler opens the shared modal.
 *
 * React caveat: if a parent component re-renders, React may overwrite our
 * wrapper. The MutationObserver reprocesses on the next tick so the styling
 * is restored — no visible flash in normal usage.
 */

const PHRASE = "Virtue Guide";
const SKIP_TAGS = new Set([
  "A", "BUTTON", "CODE", "PRE", "SCRIPT", "STYLE",
  "TEXTAREA", "INPUT", "SELECT", "OPTION", "TITLE", "SVG",
]);
const WRAPPED_CLASS = "vg-brand";

function shouldSkipParent(node, boundary) {
  let p = node.parentElement;
  while (p && p !== boundary) {
    if (SKIP_TAGS.has(p.tagName)) return true;
    if (p.classList && p.classList.contains(WRAPPED_CLASS)) return true;
    if (p.dataset && "vgSkip" in p.dataset) return true;
    p = p.parentElement;
  }
  return false;
}

function wrap(textNode) {
  const text = textNode.textContent;
  if (!text || text.indexOf(PHRASE) === -1) return;

  const parts = text.split(new RegExp(`(${PHRASE})`, "g"));
  if (parts.length === 1) return;

  const frag = document.createDocumentFragment();
  parts.forEach((p) => {
    if (p === PHRASE) {
      const el = document.createElement("button");
      el.type = "button";
      el.className = WRAPPED_CLASS;
      el.setAttribute("data-vg", "1");
      el.setAttribute("data-testid", "vg-brand");
      el.setAttribute("aria-label", "Get the Virtue Guide app");
      el.textContent = PHRASE;
      frag.appendChild(el);
    } else if (p) {
      frag.appendChild(document.createTextNode(p));
    }
  });
  textNode.parentNode.replaceChild(frag, textNode);
}

function processRoot(root) {
  if (!root) return;
  const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT, {
    acceptNode(n) {
      if (!n.textContent || n.textContent.indexOf(PHRASE) === -1) return NodeFilter.FILTER_REJECT;
      if (shouldSkipParent(n, root)) return NodeFilter.FILTER_REJECT;
      return NodeFilter.FILTER_ACCEPT;
    },
  });
  const targets = [];
  while (walker.nextNode()) targets.push(walker.currentNode);
  targets.forEach(wrap);
}

export default function VirtueGuideHighlighter({ children }) {
  const rootRef = useRef(null);
  const { pathname } = useLocation();
  const { openVirtueGuide } = useVirtueGuide();
  const enabled = !pathname.startsWith("/admin");

  useEffect(() => {
    const root = rootRef.current;
    if (!root || !enabled) return;

    // Initial pass — schedule for after layout so React finishes mounting.
    const first = requestAnimationFrame(() => processRoot(root));

    let pending = null;
    const schedule = () => {
      if (pending) return;
      pending = requestAnimationFrame(() => { pending = null; processRoot(root); });
    };

    const observer = new MutationObserver((mutations) => {
      for (const m of mutations) {
        if (m.type === "characterData" || m.type === "childList") {
          schedule();
          return;
        }
      }
    });
    observer.observe(root, { childList: true, subtree: true, characterData: true });

    const onClick = (e) => {
      const btn = e.target.closest(".vg-brand");
      if (btn && root.contains(btn)) {
        e.preventDefault();
        e.stopPropagation();
        openVirtueGuide();
      }
    };
    root.addEventListener("click", onClick);

    return () => {
      cancelAnimationFrame(first);
      if (pending) cancelAnimationFrame(pending);
      observer.disconnect();
      root.removeEventListener("click", onClick);
    };
  }, [enabled, pathname, openVirtueGuide]);

  return (
    <div ref={rootRef} className="contents">
      {children}
    </div>
  );
}
